// Components for rendering a PSI instance.

import { IBMPlexMono_400Regular } from '@expo-google-fonts/ibm-plex-mono/400Regular';
import { IBMPlexMono_500Medium } from '@expo-google-fonts/ibm-plex-mono/500Medium';
import { IBMPlexMono_600SemiBold } from '@expo-google-fonts/ibm-plex-mono/600SemiBold';
import { IBMPlexSans_400Regular } from '@expo-google-fonts/ibm-plex-sans/400Regular';
import { IBMPlexSans_500Medium } from '@expo-google-fonts/ibm-plex-sans/500Medium';
import { IBMPlexSans_600SemiBold } from '@expo-google-fonts/ibm-plex-sans/600SemiBold';
import { LoadingScreen } from 'component/banner';
import { colorSurfacePrimary } from 'component/color';
import { RenderNotificationToasts } from 'component/notification-toast';
import { SkeletonConversationScreen, SkeletonTeaserScreen } from 'component/skeleton';
import { TopBar } from 'component/topbar';
import { useFonts } from 'expo-font';
import { useFirebaseData, useFirebaseUser } from 'internal/firebase';
import suspension from 'open-modules/zdf/component/suspension/index';
import { SafeAreaSpacerBottom, SafeAreaSpacerTop } from 'platform-specific/safe-area';
import { getLanguageFromWindowUrl } from 'platform-specific/url';
import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { EMBEDDABLE_SIDEBAR_STRUCTURES, SPECIAL_SCREENS } from 'structure/constants';
import { LoginBlockedScreen, OnboardingScreen } from 'structure/login';
import { Catcher } from 'system/catcher';
import { initializeGlobalSidebarTimers } from 'util/timers';

import { useAppData } from './appdataContext';
import {
    ConfigContext,
    Datastore,
    useDatastore,
    useGlobalProperty,
    useIsLive,
    useLoaded,
    useModuleObject,
    usePersonaKey,
    usePersonaPreview,
    useSiloKey,
    WaitForData,
} from './datastore';
import { assembleConfig, assembleScreenSet, moduleMapFeatureDefaults, noDefaultFeatures } from './features';
import { WithScreenStack } from './params';
import { markPoint } from './perf-timer';
import type { StandardConfig } from './stdconfig';
import type { Structure } from './stdtypes';
import { nullS, t, undefR, undefS } from './stdtypes';
import { personaA } from './testpersonas';
import { requireParams, toBool } from './util';

export function useStandardFonts() {
    const [fontsLoaded] = useFonts({
        IBMPlexSans_400Regular,
        IBMPlexSans_500Medium,
        IBMPlexSans_600SemiBold,
        IBMPlexMono_400Regular,
        IBMPlexMono_500Medium,
        IBMPlexMono_600SemiBold,
    });
    return fontsLoaded;
}

function usePersonaPreviewForSilo({ siloKey, once = true }) {
    const fbUser = useFirebaseUser();
    const personaPreview = useFirebaseData(['silo', siloKey, 'structure', 'profile', 'instance', fbUser?.uid, 'global', 'preview']);
    if (!fbUser) return null;
    return personaPreview;
}

export function ScreenStack({ screenStack, siloKey, structureKey, instanceKey, props = {} as Record<string, any> }) {
    const s = ScreenStackStyle;

    const structure = getStructureForKey(structureKey);
    if (!structure) {
        throw new Error('Structure not found: ' + structureKey);
    }
    const readOnly =
        useFirebaseData(['silo', siloKey, 'structure', structureKey, 'instance', instanceKey, 'global', 'features', 'readonly'], {
            once: true,
            defaultValue: false,
        }) ?? true;
    const activeFeatures =
        useFirebaseData(['silo', siloKey, 'structure', structureKey, 'instance', instanceKey, 'global', 'features'], { once: readOnly }) ||
        [];
    const storedDefaultFeatures = useFirebaseData(['silo', siloKey, 'module-public', 'features', 'feature-defaults', structureKey], {
        once: readOnly,
    });
    const config = assembleConfig({ structure, activeFeatures, storedDefaultFeatures });
    let language = useFirebaseData(['silo', siloKey, 'module-public', 'language'], { once: readOnly });
    if ('french_canadian' == getLanguageFromWindowUrl()) {
        language = getLanguageFromWindowUrl();
    }
    const screenSet = assembleScreenSet({ structure, activeFeatures, storedDefaultFeatures });
    const personaPreview = usePersonaPreviewForSilo({ siloKey, once: readOnly });
    const appData = useAppData();

    const datastore = useDatastore();

    useEffect(() => {
        if (readOnly !== undefined && activeFeatures !== undefined && language !== undefined && personaPreview !== undefined) {
            markPoint(datastore, 'screenstack-loaded');
        }
    }, [readOnly, activeFeatures, language, personaPreview, datastore]);

    // FIXME: Deviant translations should be get rid of when we do the new translation system.
    // useEffect(() => {
    //     checkForDeviantTranslations(language);
    // }, [language]);

    if (!structureKey || !instanceKey || !siloKey) {
        console.error('ScreenStack missing keys', { structureKey, instanceKey, siloKey });
    }

    return (
        <View style={[s.stackHolder]}>
            <Datastore
                appData={appData}
                config={config}
                instanceKey={instanceKey}
                isAdminUi={structure.isAdminUi}
                isLive={true}
                language={language}
                personaPreview={personaPreview}
                readOnly={readOnly}
                siloKey={siloKey}
                structureKey={structureKey}
                testState={null}
                {...props}
            >
                {screenStack.map((screenInstance, index) => (
                    <WithScreenStack index={index} key={index} screenStack={screenStack}>
                        <StackedScreen index={index} screenInstance={screenInstance} screenSet={screenSet} />
                    </WithScreenStack>
                ))}
            </Datastore>
        </View>
    );
}

const ScreenStackStyle = StyleSheet.create({
    stackHolder: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
        // backgroundColor: 'white',
    },
});

export function EmbeddedInstance({
    structureKey,
    instanceKey,
    features = {} as Record<string, boolean>,
    globalParams = undefR,
    screenKey = undefS,
}) {
    const isLive = useIsLive();
    const siloKey = useSiloKey();
    const datastore = useDatastore();

    const structure = getStructureForKey(structureKey);
    if (!structure) {
        console.error('Structure not found', { structureKey });
    }

    const readOnly =
        useFirebaseData(['silo', siloKey, 'structure', structureKey, 'instance', instanceKey, 'global', 'features', 'readonly'], {
            once: true,
            defaultValue: false,
        }) ?? true;
    const selectedFeatures =
        useFirebaseData(['silo', siloKey, 'structure', structureKey, 'instance', instanceKey, 'global', 'features'], { once: readOnly }) ||
        [];
    const storedDefaultFeatures = useFirebaseData(['silo', siloKey, 'module-public', 'feature-defaults', structureKey], { once: readOnly });
    const activeFeatures = { ...selectedFeatures, ...features };
    const config = assembleConfig({ structure, activeFeatures, storedDefaultFeatures });
    const screenSet = assembleScreenSet({ structure, activeFeatures, storedDefaultFeatures });
    const screen = getScreen({ screenSet, structure, screenKey });

    if (!isLive) {
        const allInstanceData = datastore.getEmbeddedInstanceData();
        const thisInstanceData = allInstanceData?.[structureKey]?.[instanceKey];
        if (!thisInstanceData) {
            console.error('Embedded instance data not found for test', { allInstanceData, structureKey, instanceKey });
        }
        const testState = { ...datastore.props.testState, ...thisInstanceData };
        const extendedInstanceData = {
            ...datastore.props,
            globalParams,
            testState,
            config,
            structureKey,
            instanceKey,
            isLive,
            isEmbedded: true,
        };
        return <Datastore {...extendedInstanceData}>{React.createElement(screen)}</Datastore>;
    } else {
        return (
            <Datastore
                config={config}
                globalParams={globalParams}
                instanceKey={instanceKey}
                isEmbedded={true}
                isLive={isLive}
                language={datastore.props.language}
                personaPreview={datastore.props.personaPreview}
                readOnly={datastore.props.readOnly}
                siloKey={siloKey}
                structureKey={structureKey}
                testState={null}
            >
                {React.createElement(screen)}
            </Datastore>
        );
    }
}

// DRAGONS: StructureDemo currently has inconsistent config between useConfig and datastore.getConfig.
export function StructureDemo({
    siloKey = 'demo',
    structureKey,
    instanceKey = 'demo',
    screenKey = nullS,
    params = {},
    features = undefR,
    testState = undefR,
    personaPreview = personaA,
    language = 'english',
    roles = ['Owner'],
}) {
    requireParams('StructureDemo', { structureKey });
    const datastore = useDatastore();
    const [screenStack, setScreenStack] = React.useState([{ siloKey: 'demo', structureKey, instanceKey, screenKey, params }]);

    function pushSubscreen(screenKey, params) {
        setScreenStack([...screenStack, { siloKey: 'demo', structureKey, instanceKey, screenKey, params }]);
    }
    function gotoInstance({ structureKey, instanceKey, screenKey, params }) {
        setScreenStack([{ siloKey: 'demo', structureKey, instanceKey, screenKey, params }]);
    }

    function goBack() {
        if (screenStack.length == 1) {
            datastore.goBack();
        } else {
            setScreenStack(screenStack.slice(0, screenStack.length - 1));
        }
    }

    const extendedTestState = {
        ...testState,
        goBack,
        roles,
        isInSidebar: false,
        pushSubscreen,
        gotoInstance,
        globals: {
            ...testState?.globals,
            features: {
                ...noDefaultFeatures(structureKey),
                ...features,
            },
        },
    };

    return (
        <Datastore
            config={{}}
            instanceKey={instanceKey}
            isEmbedded={false}
            isLive={false}
            language={language}
            personaPreview={personaPreview}
            readOnly={false}
            siloKey={siloKey}
            structureKey={structureKey}
            testState={extendedTestState}
        >
            <StructureDemoConfiguredScreenStack screenStack={screenStack} structureKey={structureKey} />
        </Datastore>
    );
}

export function WithConfig({ config, children }) {
    return <ConfigContext.Provider value={config}>{children}</ConfigContext.Provider>;
}

// This has to be inside the Datastore, to allow the user to change the
// features (in the datastore) while playing with the demo.
export function StructureDemoConfiguredScreenStack({ structureKey, screenStack }) {
    const s = ScreenStackStyle;

    const activeFeatures = useGlobalProperty('features');
    const structure = getStructureForKey(structureKey);
    const storedDefaultFeatures = useModuleObject(moduleMapFeatureDefaults, structureKey);
    const screenSet = assembleScreenSet({ structure, activeFeatures, storedDefaultFeatures });
    const config = assembleConfig({ structure, activeFeatures, storedDefaultFeatures });

    return (
        <View style={s.stackHolder}>
            <ConfigContext.Provider value={config}>
                {screenStack.map((screenInstance, index) => (
                    <WithScreenStack index={index} key={index} screenStack={screenStack}>
                        <StackedScreen index={index} key={index} screenInstance={screenInstance} screenSet={screenSet} />
                    </WithScreenStack>
                ))}
            </ConfigContext.Provider>
        </View>
    );
}

export function StackedScreen({ screenSet, screenInstance, index = 0 }) {
    const { structureKey, instanceKey, screenKey, params } = screenInstance;
    const loaded = useLoaded();
    const datastore = useDatastore();
    const isSpecialScreen = SPECIAL_SCREENS.includes(screenKey);

    if (datastore && loaded && !isSpecialScreen) {
        initializeGlobalSidebarTimers(datastore);
    }

    const personaKey = usePersonaKey();
    const personaPreview = usePersonaPreview();
    const isSuspended = suspension.useIsSuspended();
    const needsSetup = personaKey && !personaPreview?.name && !isSpecialScreen;

    const structure = getStructureForKey(structureKey);
    const config = datastore.getConfig<StandardConfig>();
    const shouldHideTopbar = config.shouldHideTopbar;
    const screen = getScreen({ screenSet, structure, screenKey });
    const loadingScreen = getLoadingScreen({ structureKey, screenKey });

    if (!screen) {
        if (loaded) {
            // Don't show this error waiting for screen set to resolve
            console.error('Screen not found', { loaded, screenSet, structure: structure, screenKey, instanceKey, screen });
        }
        return (
            <FullScreen transparent={isSpecialScreen} zIndex={index}>
                <LoadingScreen label={t('Screen not found')} />
            </FullScreen>
        );
    }

    return (
        <FullScreen transparent={isSpecialScreen} zIndex={index}>
            {!isSpecialScreen && <SafeAreaSpacerTop />}
            {!isSpecialScreen && !shouldHideTopbar && <TopBar preventBack={toBool(needsSetup)} />}
            <WaitForData loadingScreen={loadingScreen}>
                <Catcher>
                    {needsSetup ? <OnboardingScreen /> : isSuspended ? <LoginBlockedScreen /> : React.createElement(screen, params)}
                </Catcher>
            </WaitForData>
            {!isSpecialScreen && <SafeAreaSpacerBottom />}
        </FullScreen>
    );
}

function getScreen({ screenSet, structure, screenKey }) {
    if (!screenKey) {
        return structure.screen;
    } else if (screenKey == 'teaser') {
        return structure.teaser;
    } else if (screenKey == 'overlayScreen') {
        return structure.overlayScreen;
    } else {
        return screenSet[screenKey];
    }
}

function getLoadingScreen({ structureKey, screenKey }) {
    if (screenKey === 'teaser') {
        return <SkeletonTeaserScreen />;
    }
    if (EMBEDDABLE_SIDEBAR_STRUCTURES.includes(structureKey)) {
        return <SkeletonConversationScreen />;
    }
    return <LoadingScreen />;
}

function FullScreen({ children, zIndex = 0, transparent = false }) {
    const s = FullScreenStyle;
    return (
        <View style={[s.fullScreen, { zIndex, backgroundColor: transparent ? 'transparent' : colorSurfacePrimary }]}>
            {children}
            <RenderNotificationToasts />
        </View>
    );
}

const FullScreenStyle = StyleSheet.create({
    fullScreen: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
});

// FIXME: This is a hack to avoid this module important the structures and creating
// a cycle. We should probably do this with a context, but that's going to require
// refactoring a load of stuff, so we'll just live with it for now.
let global_structures: Structure<any>[] | null = null;

// This must be called before any structures are used, at startup for tests or the app
export function setGlobalStructures(structures: Structure<any>[]) {
    global_structures = structures;
}

export function getStructureForKey(key: string | undefined): Structure<any> | null {
    if (!key) return null;
    if (!global_structures) {
        throw new Error('Global structures not set');
    }
    return global_structures.find((structure: Structure<any>) => structure.key === key) ?? null;
}
